import type { MiaPublicConfig, MiaSession } from '../types';
import { normalizeApiBaseUrl } from '../utils';

export type ApiError = {
  code: string;
  message: string;
  requestId?: string;
  httpStatus: number;
};

export class MiaApiClient {
  private baseUrl: string;

  constructor(apiBaseUrl?: string) {
    this.baseUrl = normalizeApiBaseUrl(apiBaseUrl);
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }

  async createSession(publicWidgetKey: string, externalSubjectId?: string | null): Promise<{
    session: MiaSession;
    config: MiaPublicConfig;
    tenant: { id: number; name: string };
  }> {
    const body: Record<string, unknown> = { publicWidgetKey };
    if (externalSubjectId) body.externalSubjectId = externalSubjectId;

    // Trailing slash avoids LiteSpeed/Apache DirectorySlash 301 that breaks CORS preflight.
    const json = await this.requestJson('POST', '/widget/session/', body);
    const data = json.data as Record<string, unknown>;
    const widget = (data.widget ?? {}) as Record<string, unknown>;
    const theme = (widget.theme ?? {}) as Record<string, unknown>;

    return {
      session: {
        sessionToken: String(data.sessionToken ?? ''),
        expiresAt: String(data.expiresAt ?? ''),
      },
      tenant: {
        id: Number((data.tenant as { id?: number })?.id ?? 0),
        name: String((data.tenant as { name?: string })?.name ?? ''),
      },
      config: {
        name: String(widget.name ?? 'MIA'),
        position: widget.position === 'left' ? 'left' : 'right',
        locale: String(widget.locale ?? 'es'),
        theme: {
          primaryColor: typeof theme.primaryColor === 'string' ? theme.primaryColor : '#0F766E',
        },
        welcomeMessage:
          typeof widget.welcomeMessage === 'string'
            ? widget.welcomeMessage
            : '¡Hola! ¿En qué puedo ayudarte?',
        logoUrl: typeof widget.logoUrl === 'string' ? widget.logoUrl : undefined,
      },
    };
  }

  async sendChat(params: {
    sessionToken: string;
    message: string;
    conversationId: string;
    /** Host-asserted id from setUser only — omit for technical anonymous visitors. */
    userId?: string | null;
    /** Firebase ID Token only — never send UID as a trust claim. */
    firebaseIdToken?: string | null;
  }): Promise<{ reply: string; requestId?: string }> {
    const extraHeaders: Record<string, string> = {};
    const token = params.firebaseIdToken?.trim();
    if (token) {
      extraHeaders['X-Firebase-ID-Token'] = token;
    }

    const body: Record<string, unknown> = {
      message: params.message,
      conversationId: params.conversationId,
    };
    const assertedUserId = typeof params.userId === 'string' ? params.userId.trim() : '';
    if (assertedUserId !== '') {
      body.userId = assertedUserId;
    }

    const json = await this.requestJson(
      'POST',
      '/chat/',
      body,
      params.sessionToken,
      extraHeaders,
    );

    if (json.status === 'ok') {
      const data = json.data as { message?: string };
      return { reply: String(data?.message ?? ''), requestId: json.meta?.requestId };
    }

    const err = json.error as { code?: string; message?: string } | undefined;
    throw this.toApiError(err?.code ?? 'ENGINE_ERROR', err?.message ?? 'Request failed', 422, json.meta?.requestId);
  }

  private async requestJson(
    method: string,
    path: string,
    body?: Record<string, unknown>,
    bearer?: string,
    extraHeaders?: Record<string, string>,
  ): Promise<{
    status: string;
    data?: unknown;
    error?: { code?: string; message?: string };
    meta?: { requestId?: string };
  }> {
    let response: Response;
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(extraHeaders ?? {}),
      };
      if (bearer) headers.Authorization = `Bearer ${bearer}`;

      response = await fetch(`${this.baseUrl}${path}`, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
        credentials: 'omit',
      });
    } catch {
      throw this.toApiError('NETWORK_ERROR', 'Network error', 0);
    }

    let json: {
      status?: string;
      data?: unknown;
      error?: { code?: string; message?: string };
      meta?: { requestId?: string };
    };
    try {
      json = (await response.json()) as typeof json;
    } catch {
      throw this.toApiError('NETWORK_ERROR', 'Invalid JSON response', response.status);
    }

    if (!response.ok || json.status === 'error') {
      throw this.toApiError(
        json.error?.code ?? 'INTERNAL_ERROR',
        json.error?.message ?? 'Request failed',
        response.status,
        json.meta?.requestId,
      );
    }

    return {
      status: json.status ?? 'ok',
      data: json.data,
      error: json.error,
      meta: json.meta,
    };
  }

  private toApiError(code: string, message: string, httpStatus: number, requestId?: string): ApiError {
    return { code, message, httpStatus, requestId };
  }
}

export function isApiError(err: unknown): err is ApiError {
  return typeof err === 'object' && err !== null && 'code' in err && 'httpStatus' in err;
}
